import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import type { AddItem } from '../../types/actions';
import TextAreaField from '../ui/TextAreaField';
import { api } from '../../api/client';
import { parseFormDraftResponse } from './aiFormDraftParser';
import AiDraftAssistPanel from './AiDraftAssistPanel';

export default function OrganizationForm({
  addItem,
  submitRef,
  showSubmit = true,
}: {
  addItem: AddItem;
  submitRef?: React.MutableRefObject<(() => void) | null>;
  showSubmit?: boolean;
}) {
  const [org, setOrg] = useState({ name: '', type: '', goals: '', members: '', notes: '' });
  const [errors, setErrors] = useState<{ name?: string }>({});
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiError, setAiError] = useState('');
  const [isDraftingWithAi, setIsDraftingWithAi] = useState(false);

  const draftWithAi = useCallback(async () => {
    if (isDraftingWithAi) return;

    const prompt = aiPrompt.trim();
    const hasCurrentDraft = [org.name, org.type, org.goals, org.members, org.notes].some((value) => value.trim().length > 0);
    if (!prompt && !hasCurrentDraft) {
      setAiError('Add a prompt or some draft text before generating.');
      return;
    }

    const systemPrompt = `You draft organization records for tabletop campaigns.
Return ONLY JSON with keys: name, type, goals, members, notes.
Rules:
- Keep content concise and useful for session prep.
- members should be plain text listing notable people.
- Do not include extra keys or markdown fences.`;
    const userPrompt = `Create or refine this organization draft.
Current draft:
- name: ${org.name || '(empty)'}
- type: ${org.type || '(empty)'}
- goals: ${org.goals || '(empty)'}
- members: ${org.members || '(empty)'}
- notes: ${org.notes || '(empty)'}
GM prompt: ${prompt || '(none)'}`;

    setIsDraftingWithAi(true);
    setAiError('');
    try {
      const result = await api.sendAssistantMessage(
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        0.6
      );
      const parsed = parseFormDraftResponse(result.content, ['name', 'type', 'goals', 'members', 'notes']);
      if (!parsed) {
        throw new Error('AI returned an unexpected format. Try a more specific prompt.');
      }

      setOrg((previous) => ({
        ...previous,
        name: parsed.name ?? previous.name,
        type: parsed.type ?? previous.type,
        goals: parsed.goals ?? previous.goals,
        members: parsed.members ?? previous.members,
        notes: parsed.notes ?? previous.notes,
      }));
    } catch (error) {
      setAiError(error instanceof Error ? error.message : 'Failed to generate organization draft with AI.');
    } finally {
      setIsDraftingWithAi(false);
    }
  }, [aiPrompt, isDraftingWithAi, org.goals, org.members, org.name, org.notes, org.type]);

  const handleSubmit = useCallback(() => {
    if (!org.name.trim()) {
      setErrors({ name: 'Organization name is required.' });
      return;
    }
    addItem('organizations', org);
    setOrg({ name: '', type: '', goals: '', members: '', notes: '' });
    setAiPrompt('');
    setAiError('');
    setErrors({});
  }, [addItem, org]);

  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit;
    }
    return () => {
      if (submitRef) {
        submitRef.current = null;
      }
    };
  }, [submitRef, handleSubmit]);

  return (
    <div className="bg-[var(--accent-muted)] p-4 rounded-lg border border-[var(--glass-border)] mb-4">
      <div className="field-block">
        <label className="field-label">Organization Name</label>
        <input
          type="text"
          placeholder="Organization Name"
          value={org.name}
          onChange={(e) => {
            setOrg({ ...org, name: e.target.value });
            if (errors.name) setErrors({});
          }}
          className={`input-field ${errors.name ? 'input-error' : ''}`}
        />
        <div className="field-meta">
          <span className={errors.name ? 'field-error' : 'field-hint'}>
            {errors.name || 'Required.'}
          </span>
        </div>
      </div>
      <div className="field-block">
        <label className="field-label">Type</label>
        <input
          type="text"
          placeholder="Guild, Faction, Secret Society…"
          value={org.type}
          onChange={(e) => setOrg({ ...org, type: e.target.value })}
          className="input-field"
        />
      </div>
      <TextAreaField
        label="Goals and Purpose"
        placeholder="Goals and Purpose"
        value={org.goals}
        onChange={(value) => setOrg({ ...org, goals: value })}
        hint="Mission, tactics, and priorities."
        showCount
        minRows={4}
      />
      <div className="field-block">
        <label className="field-label">Notable Members</label>
        <input
          type="text"
          placeholder="Notable Members"
          value={org.members}
          onChange={(e) => setOrg({ ...org, members: e.target.value })}
          className="input-field"
        />
      </div>
      <AiDraftAssistPanel
        value={aiPrompt}
        onChange={(value) => {
          setAiPrompt(value);
          if (aiError) setAiError('');
        }}
        onGenerate={() => void draftWithAi()}
        isGenerating={isDraftingWithAi}
        error={aiError}
        hint="Ask for structure, agenda, internal tensions, and key operatives."
      />
      <TextAreaField
        label="Notes"
        placeholder="Notes"
        value={org.notes}
        onChange={(value) => setOrg({ ...org, notes: value })}
        hint="Rumors, alliances, and pressure points."
        showCount
        minRows={4}
      />
      {showSubmit && (
        <button
          onClick={handleSubmit}
          className="btn-primary-solid flex items-center gap-2"
        >
          <Plus size={16} /> Add Organization
        </button>
      )}
    </div>
  );
}
